import { useState } from 'react';
import { toast } from 'sonner';

import type { WorkspaceVisibility } from '@/lib/api/generated/model';
import { ConfirmDestructiveDialog } from '@/shared/components/ConfirmDestructiveDialog';
import {
  VISIBILITY_LINE,
  VISIBILITY_NAME,
} from '@/features/workspaces/accessCopy';

import { useUpdateWorkspaceSettings } from '../hooks/useWorkspaceMembers';
import { SettingsRow } from './SettingsRow';
import { VisibilityChoice } from './VisibilityChoice';

export interface GeneralSectionProps {
  workspaceId: string;
  name: string;
  visibility: WorkspaceVisibility;
  /** People who follow one of its tasks without being members. */
  nonMemberFollowers: number;
  /** How many members it has, when the page has read them. */
  memberCount?: number;
  /** The organization's size, for the Organization card's count (spec 194). */
  organizationCount?: number;
  /** Its owners' names, for the statement someone who cannot manage reads. */
  owners?: string[];
  /** Owners and org admins change these; everyone else reads them. */
  canManage: boolean;
}

/** How long a visibility change can be taken back (spec 193 US2). */
export const VISIBILITY_UNDO_MS = 10_000;

/** What a workspace is after a change, for the toast: "Payments is now private." */
function nowSentence(name: string, visibility: WorkspaceVisibility): string {
  return visibility === 'private'
    ? `${name} is now private.`
    : `${name} is now shared with the organization.`;
}

/** What going Private does, with the counts the page has (spec 193 US2). */
function privateConsequence(
  memberCount: number | undefined,
  nonMemberFollowers: number,
): string {
  const who =
    memberCount === undefined
      ? 'Only its members will see it.'
      : memberCount === 1
        ? 'Only its 1 member will see it.'
        : `Only its ${String(memberCount)} members will see it.`;
  const followers =
    nonMemberFollowers === 0
      ? '.'
      : nonMemberFollowers === 1
        ? '; 1 of them follows tasks here and stops following.'
        : `; ${String(nonMemberFollowers)} of them follow tasks here and stop following.`;
  return `${who} People who are not members lose access at once${followers}`;
}

function lowerFirst(text: string): string {
  return text.charAt(0).toLowerCase() + text.slice(1);
}

/**
 * Who can see it, said plainly to someone who cannot change it (spec 194 US2):
 * no greyed-out radios offering what they may not do.
 */
function readOnlyStatement(
  visibility: WorkspaceVisibility,
  owners: string[] | undefined,
): string {
  const who = `${VISIBILITY_NAME[visibility]}: ${lowerFirst(VISIBILITY_LINE[visibility])}`;
  if (owners === undefined || owners.length === 0) return who;
  return `${who} ${owners.length === 1 ? 'Owner' : 'Owners'}: ${owners.join(', ')}.`;
}

/**
 * Who besides its members may see the workspace (spec 186). Its name moved into
 * the page's band, edited in place (spec 194 US2).
 *
 * Who can see a workspace is never one stray click (spec 193 US2): either
 * direction asks first and says what changes, and the toast that confirms it
 * offers Undo for ten seconds. Undo is the reverse change, logged like any other.
 */
export function GeneralSection({
  workspaceId,
  name,
  visibility,
  nonMemberFollowers,
  memberCount,
  organizationCount,
  owners,
  canManage,
}: GeneralSectionProps) {
  const update = useUpdateWorkspaceSettings();
  const [asking, setAsking] = useState<WorkspaceVisibility | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);

  const choose = (next: WorkspaceVisibility) => {
    if (next === visibility) return;
    setRefusal(null);
    setAsking(next);
  };

  // `mutateAsync`, not `mutate`: the section remounts once the workspace is read
  // again with its new visibility, and Undo is clicked after that. A promise
  // settles regardless of who is still listening.
  const undo = (previous: WorkspaceVisibility) => {
    update.mutateAsync({ workspaceId, visibility: previous }).then(
      () => {
        toast.success(
          previous === 'private'
            ? `${name} is private again.`
            : `${name} is shared with the organization again.`,
        );
      },
      (failure: unknown) => {
        toast.error(
          failure instanceof Error
            ? failure.message
            : 'The workspace could not be updated.',
        );
      },
    );
  };

  const confirm = () => {
    if (asking === null) return;
    const next = asking;
    const previous = visibility;
    update.mutate(
      { workspaceId, visibility: next },
      {
        onSuccess: () => {
          setAsking(null);
          toast.success(nowSentence(name, next), {
            duration: VISIBILITY_UNDO_MS,
            action: {
              label: 'Undo',
              onClick: () => {
                undo(previous);
              },
            },
          });
        },
        onError: (failure) => {
          setRefusal(failure.message);
        },
      },
    );
  };

  if (!canManage) {
    return (
      <SettingsRow
        id="general-heading"
        title="Who can see it"
        lead="Private, or shared with the organization to read."
      >
        <p className="text-foreground text-sm">
          {readOnlyStatement(visibility, owners)}
        </p>
      </SettingsRow>
    );
  }

  return (
    <SettingsRow
      id="general-heading"
      title="Who can see it"
      lead="Private, or shared with the organization to read. Either way, only members change anything."
    >
      <VisibilityChoice
        value={visibility}
        onChoose={choose}
        pending={update.isPending ? asking : null}
        memberCount={memberCount}
        organizationCount={organizationCount}
      />

      <ConfirmDestructiveDialog
        open={asking !== null}
        title={
          asking === 'organization'
            ? 'Share with the organization?'
            : 'Make this workspace private?'
        }
        confirmLabel={
          asking === 'organization'
            ? 'Share with the organization'
            : 'Make private'
        }
        pendingLabel={
          asking === 'organization' ? 'Sharing…' : 'Making private…'
        }
        confirmVariant={asking === 'organization' ? 'default' : 'destructive'}
        isPending={update.isPending}
        error={refusal}
        onConfirm={confirm}
        onCancel={() => {
          setAsking(null);
          setRefusal(null);
        }}
      >
        {asking === 'organization'
          ? 'Everyone in the organization will be able to see its work; only members can change it.'
          : privateConsequence(memberCount, nonMemberFollowers)}
      </ConfirmDestructiveDialog>
    </SettingsRow>
  );
}
